import React, { useState, useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import { Home, Building, CalendarDays, Clock, FileText, MessageCircle, BarChart3, Settings, Monitor, Users, Database, CheckCircle } from 'lucide-react';
import { useAuthStore } from '../store';

interface User {
  _id?: string;
  id?: number;
  username: string;
  email: string;
  role: string;
  permissions?: string[];
}

const Sidebar: React.FC = () => {
  const { user } = useAuthStore();
  const userPermissions = user?.permissions || [];
  
  // Menü sırasını localStorage'dan al
  const getMenuItems = () => {
    const savedOrder = localStorage.getItem('menuOrder');
    if (savedOrder) {
      try {
        return JSON.parse(savedOrder);
      } catch (error) {
        console.error('Menü sırası yüklenirken hata:', error);
      }
    }
    
    // Varsayılan menü sırası
    return [
      { id: 'home', label: 'Ana Sayfa', icon: '🏠', to: '/', enabled: true },
      { id: 'facilities', label: 'Tesisler', icon: '🏢', to: '/facilities', enabled: true },
      { id: 'daily', label: 'Günlük İş Programı', icon: '📅', to: '/daily-checks', enabled: true },
      { id: 'weekly', label: 'Toplam Yapılan İşler', icon: '⏰', to: '/haftalik', enabled: true },
      { id: 'ybs-work-program', label: 'YBS İş Programı', icon: '📋', to: '/ybs-work-program', enabled: true },
  { id: 'ybs-approvals', label: 'YBS Onay Ekranları', icon: '✅', to: '/ybs-approvals', enabled: true },
        { id: 'bagkart', label: 'BagKart Yönetimi', icon: '💳', to: '/bagkart', enabled: true },
  { id: 'bagkart-business-info', label: 'BağKart Üye İşyeri Bilgileri', icon: '🏪', to: '/bagkart-business-info', enabled: true },
  { id: 'reports', label: 'Raporlar', icon: '📊', to: '/reports', enabled: true },
      { id: 'messages', label: 'Mesaj Yönetimi', icon: '💬', to: '/messages', enabled: true },
      { id: 'bagtv', label: 'BağTV Yönetim', icon: '📺', to: '/bagtv', enabled: true },
      { id: 'data-control', label: 'Veri Kontrol', icon: '🗄️', to: '/data-control', enabled: true },
      { id: 'approvals', label: 'Onay Yönetimi', icon: '✅', to: '/approvals', enabled: true },
      { id: 'completed-works', label: 'Yapılan İşler', icon: '✅', to: '/completed-works', enabled: true },
      { id: 'user-management', label: 'Kullanıcı Yönetimi', icon: '👥', to: '/user-management', enabled: true },
      { id: 'settings', label: 'Ayarlar', icon: '⚙️', to: '/settings', enabled: true },
    ];
  };

  const [menuItems, setMenuItems] = useState(getMenuItems());
  const [forceUpdate, setForceUpdate] = useState(0);

  // Menü değişikliklerini dinle
  useEffect(() => {
    const handleMenuOrderChange = () => {
      setMenuItems(getMenuItems());
      setForceUpdate(prev => prev + 1); // Force update
    };

    // localStorage değişikliklerini dinle
    window.addEventListener('storage', handleMenuOrderChange);
    
    // Custom event dinle
    window.addEventListener('menuOrderChanged', handleMenuOrderChange);

    // Sayfa yüklendiğinde de güncelle
    handleMenuOrderChange();

    return () => {
      window.removeEventListener('storage', handleMenuOrderChange);
      window.removeEventListener('menuOrderChanged', handleMenuOrderChange);
    };
  }, []);

  // İkon mapping'i
  const getIcon = (icon: string) => {
    const iconMap: { [key: string]: React.ReactNode } = {
      '🏠': <Home size={18} />,
      '🏢': <Building size={18} />,
      '📅': <CalendarDays size={18} />,
      '⏰': <Clock size={18} />,
      '📊': <BarChart3 size={18} />,
      '💬': <MessageCircle size={18} />,
      '📺': <Monitor size={18} />,
      '🗄️': <Database size={18} />,
      '✅': <CheckCircle size={18} />,
      '👥': <Users size={18} />,
      '💳': <FileText size={18} />,
      '🏪': <Building size={18} />,
      '📋': <FileText size={18} />,
      '📱': <MessageCircle size={18} />,
      '⚙️': <Settings size={18} />,
    };
    return iconMap[icon] || <Home size={18} />;
  };

  // Kullanıcının yetkilerine göre menü öğelerini filtrele
  const filteredMenuItems = menuItems.filter(item => {
    if (!item.enabled) return false;
    
    // Admin kullanıcılar tüm modülleri görebilir
    if (user?.role === 'admin') {
      return true;
    }
    
    // User kullanıcılar sadece kendi yetkilerine sahip modülleri görebilir
    const hasPermission = Array.isArray(userPermissions) && userPermissions.includes(item.label);
    
    return hasPermission;
  });

  return (
    <aside className="w-64 bg-white h-full max-h-screen shadow-md flex flex-col overflow-hidden border-r border-gray-100">
      <nav className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-3 py-3 space-y-0.5">
        {filteredMenuItems.map(item => (
          <NavLink
            key={item.to}
            to={item.to}
            className={({ isActive }) =>
              `flex items-center gap-2.5 px-2.5 py-1.5 rounded-lg transition-colors text-sm leading-snug
              ${isActive ? 'bg-blue-100 text-blue-700 font-semibold' : 'text-gray-700 hover:bg-gray-100'}`
            }
          >
            <span className="shrink-0 flex items-center justify-center w-5 h-5">
              {getIcon(item.icon)}
            </span>
            <span className="break-words">{item.label}</span>
          </NavLink>
        ))}
      </nav>
      <div className="flex-shrink-0 px-3 py-2 text-[11px] text-gray-400 border-t border-gray-100">
        Sistem Aktif
        <br />
        Tüm kontroller güncel
      </div>
    </aside>
  );
};

export default Sidebar; 